<template>
  <div class="site-create-step2">
    <div class="form-card">
      <div
        class="drop-zone drawing-stage-base"
        :class="{ dragging: isDragging, 'has-image': !!preview }"
        @dragenter.prevent="onDragEnter"
        @dragover.prevent="onDragOver"
        @dragleave.prevent="onDragLeave"
        @drop.prevent="onDrop"
        @click="openFileDialog"
      >
        <input
          ref="fileInput"
          class="file-input"
          type="file"
          accept="image/*"
          @change="onFileChange"
        />

        <!-- 업로드된 이미지 미리보기 -->
        <div
          v-if="preview"
          class="image-view drawing-image-layer-base"
        >
          <img
            :src="preview.url"
            class="preview-full drawing-image-base"
            alt="drawing"
          />

          <button
            class="remove-fab"
            type="button"
            @click.stop="removeFile"
          >
            삭제
          </button>
        </div>

        <!-- 빈 상태 -->
        <div v-else class="empty-state">
          <div class="plus-circle">+</div>
          <div class="empty-text">이미지 불러오기</div>
          <div class="empty-sub">드래그하거나 클릭해서 업로드하세요</div>
        </div>
      </div>

      <div class="bottom-actions">
        <button class="btn primary" type="button" @click="onNext" :disabled="saving">
          {{ saving ? "저장 중..." : "다음으로" }}
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import api from '@/api/axios'

export default {
  name: "CreateSiteStep2",

  props: {
    mode: {
      type: String,
      default: "create",
    },
    formValue: {
      type: Object,
      default() {
        return {}
      },
    },
  },

  data() {
    return {
      isDragging: false,
      preview: null,
      isPickingFile: false,
      saving: false,
      ownedPreviewUrls: new Set(),
    }
  },

  computed: {
    isEditMode() {
      return this.mode === "edit"
    },
  },

  mounted() {
    this.restorePreviewFromForm()
  },

  watch: {
    formValue: {
      handler() {
        this.restorePreviewFromForm()
      },
      deep: true,
    },
  },

  methods: {
    createOwnedPreviewUrl(file) {
      const url = URL.createObjectURL(file)
      this.ownedPreviewUrls.add(url)
      return url
    },

    revokeOwnedPreviewUrl(url) {
      if (!url) return

      if (this.ownedPreviewUrls.has(url)) {
        URL.revokeObjectURL(url)
        this.ownedPreviewUrls.delete(url)
      }
    },

    revokeOwnedPreviewUrls() {
      for (const url of this.ownedPreviewUrls) {
        URL.revokeObjectURL(url)
      }
      this.ownedPreviewUrls.clear()
    },

    restorePreviewFromForm() {
      const drawing = this.formValue?.drawing ?? null
      const drawingAttachFileNo = this.formValue?.drawingAttachFileNo ?? null

      if (this.preview?.isNew) {
        this.revokeOwnedPreviewUrl(this.preview?.url)
      }

      // 1. 새로 고른 파일 우선
      if (drawing instanceof File) {
        const url = this.createOwnedPreviewUrl(drawing)

        this.preview = {
          file: drawing,
          url,
          name: drawing.name || "drawing",
          size: drawing.size || 0,
          isNew: true,
          drawingAttachFileNo: null,
        }
        return
      }

      // 2. 기존 서버 도면 복원
      if (drawingAttachFileNo) {
        this.preview = {
          file: null,
          url: `/app/file/image/${drawingAttachFileNo}`,
          name: "기존 도면",
          size: 0,
          isNew: false,
          drawingAttachFileNo,
        }
        return
      }

      // 3. 아무것도 없으면 비움
      this.preview = null
    },

    openFileDialog() {
      this.isPickingFile = true
      window.addEventListener("keydown", this.blockEscapeWhilePicking, true)
      window.addEventListener("focus", this.onDialogClosed, { once: true })

      if (this.$refs.fileInput) {
        this.$refs.fileInput.click()
      }
    },

    blockEscapeWhilePicking(e) {
      if (!this.isPickingFile) return

      if (e.key === "Escape") {
        e.preventDefault()
        e.stopPropagation()
      }
    },

    onDialogClosed() {
      this.isPickingFile = false
      window.removeEventListener("keydown", this.blockEscapeWhilePicking, true)
    },

    onFileChange(e) {
      const files = Array.from(e.target.files || [])
      this.setSingleFile(files)
      e.target.value = ""
    },

    onDragEnter() {
      this.isDragging = true
    },

    onDragOver() {
      this.isDragging = true
    },

    onDragLeave() {
      this.isDragging = false
    },

    onDrop(e) {
      this.isDragging = false
      const files = Array.from(e.dataTransfer.files || [])
      this.setSingleFile(files)
    },

    setSingleFile(files) {
      const imageFile = files.find(file => file && file.type && file.type.startsWith("image/"))
      if (!imageFile) return

      if (this.preview?.isNew) {
        this.revokeOwnedPreviewUrl(this.preview.url)
      }

      const url = this.createOwnedPreviewUrl(imageFile)

      this.preview = {
        file: imageFile,
        url,
        name: imageFile.name,
        size: imageFile.size,
        isNew: true,
        drawingAttachFileNo: null,
      }
    },

    removeFile() {
      if (this.preview?.isNew) {
        this.revokeOwnedPreviewUrl(this.preview.url)
      }

      this.preview = null
    },

    async onNext() {
      if (!this.formValue?.siteId) {
        alert("먼저 기본 정보에서 site가 생성되어야 합니다.")
        return
      }

      this.saving = true

      try {
        // 도면 제거
        if (!this.preview) {
          const payload = {
            id: this.formValue.siteId,
            drawingAttachFileNo: null,
            drawingDeleted: true,
          }

          const formData = new FormData()
          formData.append("paramsObj", JSON.stringify(payload))

          const res = await api.post("/app/site/update", formData)

          this.$emit("next", {
            drawing: null,
            drawingAttachFileNo: null,
            drawingDeleted: true,
            siteId: this.formValue.siteId,
            serverResult: res.data?.result ?? null,
          })
          return
        }

        // 기존 서버 도면 유지
        if (!this.preview.file && this.preview.drawingAttachFileNo) {
          this.$emit("next", {
            drawing: null,
            drawingAttachFileNo: this.preview.drawingAttachFileNo,
            drawingDeleted: false,
            siteId: this.formValue.siteId,
          })
          return
        }

        // 새 파일 업로드
        if (this.preview.file) {
          const payload = {
            id: this.formValue.siteId,
            drawingDeleted: false,
            drawingAttachFileNo: this.formValue?.drawingAttachFileNo ?? null,
          }

          const formData = new FormData()
          formData.append("paramsObj", JSON.stringify(payload))
          formData.append("siteDrawingImageFile", this.preview.file)

          const res = await api.post("/app/site/uploadSiteDrawingImageFile", formData)

          this.$emit("next", {
            drawing: this.preview.file,
            drawingAttachFileNo: res.data?.result?.drawingAttachFileNo ?? null,
            drawingDeleted: false,
            siteId: this.formValue.siteId,
            serverResult: res.data?.result ?? null,
          })
        }
      } catch (e) {
        console.error(e)
        alert("현장 도면 처리 실패")
      } finally {
        this.saving = false
      }
    },
  },

  beforeUnmount() {
    this.revokeOwnedPreviewUrls()
    window.removeEventListener("keydown", this.blockEscapeWhilePicking, true)
  },
}
</script>

<style scoped>
@import "@/styles/drawing/drawing-common.css";

.site-create-step2 {
  --bg: #ffffff;
  --text: #111827;
  --muted: #6b7280;
  --line: #e5e7eb;
  --line2: #d1d5db;

  background: var(--bg);
  padding: 8px 20px 20px;
  box-sizing: border-box;

  display: flex;
  flex-direction: column;
  height: 100%;
  min-height: 0;
  overflow: auto;
}

.form-card {
  margin-top: 8px;
  background: #ffffff;
  border-radius: 12px;

  display: flex;
  flex-direction: column;
  flex: 1;
  min-height: 0;
}

.drop-zone {
  width: 100%;
  flex: 1 1 auto;
  min-height: 0;

  border: 1px solid var(--line2);
  border-radius: 10px;
  background: #ffffff;
  cursor: pointer;
  position: relative;
  overflow: hidden;

  display: flex;
  align-items: center;
  justify-content: center;
}

.drop-zone.dragging {
  border-color: var(--color-primary);
  box-shadow: 0 0 0 4px rgba(37, 99, 235, 0.12);
}

.drop-zone.has-image {
  background: #f8fafc;
}

.file-input {
  display: none;
}

.empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 10px;
  color: var(--muted);
  text-align: center;
  padding: 24px;
}

.plus-circle {
  width: 100px;
  height: 100px;
  border-radius: 999px;
  border: 2px dashed #fca5a5;
  color: #ef4444;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 32px;
  line-height: 1;
}

.empty-text {
  font-size: 1rem;
  font-weight: 800;
  color: var(--muted);
}

.empty-sub {
  font-size: 0.8rem;
  color: #9ca3af;
}

.image-view {
  position: absolute;
  inset: 0;
  padding: 0;
  box-sizing: border-box;

  display: flex;
  align-items: center;
  justify-content: center;
}

.preview-full {
  width: 100%;
  height: 100%;
  display: block;
  object-fit: contain;
}

.remove-fab {
  position: absolute;
  right: 18px;
  top: 18px;
  height: 34px;
  padding: 0 12px;
  border-radius: 10px;
  border: 1px solid var(--line2);
  background: rgba(255, 255, 255, 0.92);
  cursor: pointer;
  font-size: 0.85rem;
  color: #dc2626;
  z-index: 5;
}

.remove-fab:hover {
  background: #fef2f2;
  border-color: #fecaca;
}

.bottom-actions {
  margin-top: 12px;
  padding-top: 8px;

  display: flex;
  justify-content: flex-end;
  gap: 8px;
  flex-shrink: 0;
}

.btn {
  height: 38px;
  padding: 0 16px;
  border-radius: 10px;
  border: 1px solid var(--line2);
  background: #ffffff;
  cursor: pointer;
  font-size: 0.9rem;
  color: var(--text);
  transition: all 0.15s ease;
  white-space: nowrap;
}

.btn:hover {
  background: #f3f4f6;
}

.btn.primary {
  background: var(--color-primary);
  border-color: var(--color-primary);
  color: #ffffff;
}

.btn.primary:hover {
  background: var(--color-primary-border);
  border-color: var(--color-primary-border);
}

.btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

@media (max-width: 1400px) {
  .site-create-step2 {
    padding: 8px 16px 18px;
  }
}

@media (max-width: 1024px) {
  .site-create-step2 {
    padding: 8px 14px 16px;
  }

  .remove-fab {
    right: 14px;
    top: 14px;
  }
}
</style>